import { create } from "zustand";
import type {
  PreLessonValues,
  StudentSubmissionValues,
  TranscriptMessageValues,
} from "@live-writing/models";
import { ChatMode, StudentSubmissionStatus } from "@live-writing/models";
import StudentService from "../lib/student-service";

interface StudentStore {
  preLessonCode: string | null;
  preLesson: PreLessonValues | null;
  submission: StudentSubmissionValues | null;
  loading: boolean;
  error: string | null;
  chatMode: ChatMode | null;
  clarifyMessages: TranscriptMessageValues[];
  quizMessages: TranscriptMessageValues[];
  clarifyResponseId: string | null;
  quizResponseId: string | null;
  timeSpentSeconds: number;
  isSending: boolean;
  isQuizComplete: boolean;
  isGeneratingResults: boolean;
  quizResults: StudentSubmissionValues | null;
  openedMaterials: number[];
  initialise: (preLessonCode: string, submissionId?: number) => Promise<void>;
  registerStudent: (firstName: string, lastNameInitial: string) => Promise<number>;
  sendMessage: (message: string) => Promise<void>;
  startQuiz: () => Promise<void>;
  startClarify: () => void;
  completeQuiz: () => Promise<void>;
  exitChat: () => void;
  incrementTime: () => void;
  syncTimeToServer: () => Promise<void>;
  markMaterialOpened: (index: number) => void;
  allMaterialsOpened: () => boolean;
}

const initialState = {
  preLessonCode: null as string | null,
  preLesson: null as PreLessonValues | null,
  submission: null as StudentSubmissionValues | null,
  loading: true,
  error: null as string | null,
  chatMode: null as ChatMode | null,
  clarifyMessages: [] as TranscriptMessageValues[],
  quizMessages: [] as TranscriptMessageValues[],
  clarifyResponseId: null as string | null,
  quizResponseId: null as string | null,
  timeSpentSeconds: 0,
  isSending: false,
  isQuizComplete: false,
  isGeneratingResults: false,
  quizResults: null as StudentSubmissionValues | null,
  openedMaterials: [] as number[],
};

export const useStudentStore = create<StudentStore>()((set, get) => ({
  ...initialState,

  initialise: async (preLessonCode: string, submissionId?: number) => {
    set({ ...initialState, preLessonCode, loading: true });

    try {
      const { pre_lesson } = await StudentService.getPreLessonByCode(preLessonCode);
      set({ preLesson: pre_lesson });

      if (submissionId) {
        try {
          const { student_submission } = await StudentService.getStudentSubmission(submissionId);
          set({
            submission: student_submission,
            timeSpentSeconds: student_submission.time_spent_seconds || 0,
            openedMaterials: student_submission.opened_materials || [],
          });
        } catch {
          set({ error: "Submission not found" });
        }
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to load pre-lesson" });
    } finally {
      set({ loading: false });
    }
  },

  registerStudent: async (firstName: string, lastNameInitial: string) => {
    const { preLessonCode } = get();
    if (!preLessonCode) throw new Error("No pre-lesson code");

    try {
      set({ loading: true, error: null });
      const { student_submission } = await StudentService.createStudentSubmission(
        preLessonCode,
        firstName,
        lastNameInitial
      );
      set({
        submission: student_submission,
        timeSpentSeconds: student_submission.time_spent_seconds || 0,
      });
      return student_submission.id;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to register" });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  sendMessage: async (message: string) => {
    const { submission, chatMode, isSending } = get();
    if (!submission || !chatMode || isSending) return;

    const messagesKey = chatMode === ChatMode.QUIZ ? "quizMessages" : "clarifyMessages";
    const responseIdKey = chatMode === ChatMode.QUIZ ? "quizResponseId" : "clarifyResponseId";
    const previousResponseId = get()[responseIdKey] ?? undefined;

    set({ isSending: true });
    set((state) => ({
      [messagesKey]: [...state[messagesKey], { role: "user", content: message }],
    }));

    try {
      const { response_id } = await StudentService.startChatJob(submission.id, message, chatMode, previousResponseId);

      set({ [responseIdKey]: response_id });

      const poll = async () => {
        try {
          const result = await StudentService.pollChatJob(response_id, chatMode, submission.id);

          if (result.status === "completed") {
            const quizNowComplete = chatMode === ChatMode.QUIZ && result.is_complete;
            set((state) => ({
              [messagesKey]: quizNowComplete
                ? state[messagesKey]
                : [...state[messagesKey], { role: "assistant", content: result.response! }],
              submission: result.student_submission!,
              isQuizComplete: quizNowComplete ? true : state.isQuizComplete,
              isSending: false,
            }));
          } else if (result.status === "error") {
            set((state) => ({
              [messagesKey]: state[messagesKey].slice(0, -1),
              error: result.error || "Failed to process message",
              isSending: false,
            }));
          } else {
            setTimeout(poll, 1000);
          }
        } catch (err) {
          set((state) => ({
            [messagesKey]: state[messagesKey].slice(0, -1),
            error: err instanceof Error ? err.message : "Failed to poll response",
            isSending: false,
          }));
        }
      };

      setTimeout(poll, 1000);
    } catch (err) {
      set((state) => ({
        [messagesKey]: state[messagesKey].slice(0, -1),
        error: err instanceof Error ? err.message : "Failed to send message",
        isSending: false,
      }));
    }
  },

  startQuiz: async () => {
    const { submission, quizMessages } = get();

    set({ chatMode: ChatMode.QUIZ, isQuizComplete: false });

    if (!submission || quizMessages.length > 0) return;

    set({ isSending: true });

    try {
      const { response_id } = await StudentService.startChatJob(submission.id, "", ChatMode.QUIZ);

      set({ quizResponseId: response_id });

      const poll = async () => {
        try {
          const result = await StudentService.pollChatJob(response_id, ChatMode.QUIZ, submission.id);

          if (result.status === "completed") {
            set((state) => ({
              quizMessages: [...state.quizMessages, { role: "assistant", content: result.response! }],
              submission: result.student_submission!,
              isSending: false,
            }));
          } else if (result.status === "error") {
            set({
              error: result.error || "Failed to start quiz",
              isSending: false,
            });
          } else {
            setTimeout(poll, 2000);
          }
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to poll response",
            isSending: false,
          });
        }
      };

      setTimeout(poll, 1000);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to start quiz",
        isSending: false,
      });
    }
  },

  startClarify: () => {
    set({ chatMode: ChatMode.CLARIFY });
  },

  exitChat: () => {
    set({ chatMode: null });
  },

  completeQuiz: async () => {
    const { submission } = get();
    if (!submission) return;

    try {
      set({ isGeneratingResults: true });
      const { student_submission } = await StudentService.completeSubmission(submission.id);
      set({ submission: student_submission, quizResults: student_submission });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to complete quiz" });
    } finally {
      set({ isGeneratingResults: false });
    }
  },

  incrementTime: () => {
    set((state) => ({ timeSpentSeconds: state.timeSpentSeconds + 1 }));
  },

  syncTimeToServer: async () => {
    const { submission, timeSpentSeconds } = get();
    if (!submission || submission.status === StudentSubmissionStatus.COMPLETED) return;
    await StudentService.updateStudentSubmission(submission.id, {
      time_spent_seconds: timeSpentSeconds,
    });
  },

  markMaterialOpened: (index: number) => {
    const { openedMaterials, submission } = get();
    if (openedMaterials.includes(index)) return;

    const updated = [...openedMaterials, index];
    set({ openedMaterials: updated });

    if (submission) {
      StudentService.updateStudentSubmission(submission.id, { opened_materials: updated });
    }
  },

  allMaterialsOpened: () => {
    const { openedMaterials, preLesson } = get();
    if (!preLesson?.materials || preLesson.materials.length === 0) return true;
    return preLesson.materials.every((_, index) => openedMaterials.includes(index));
  },
}));
